import React, {useEffect, useState, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {naverBatchStatusUpdateAction, pixelStatusUpdateAction, loadSystemSettingAction, updateSystemStatusResetAction} from '../../reducers/setting';
import {loadMeAction} from '../../reducers/user';
import UIKit from '../../Components/UIKit';
import Header from '../../Components/Widget/Header';
import Loader from '../../Components/Widget/Loader';

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
    const hours = Math.floor(i / 2).toString().padStart(2, '0');
    const minutes = (i % 2 === 0 ? '00' : '30');
    return `${hours}:${minutes}`;
});

const System = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {me, loadMeLoading, loadMeDone, loadMeError} = useSelector(state => state.user);
  const {
      naverBatchStatusUpdateDone, 
      naverBatchStatusUpdateData,
      pixelStatusUpdateDone, 
      pixelStatusUpdateData,
      loadSystemSettingLoading, 
      loadSystemSettingDone, 
      systemSetting
  } = useSelector(state => state.setting);

  useEffect(() => {
    dispatch(loadMeAction());
    dispatch(loadSystemSettingAction());
  }, []);

  // 업데이트 성공 후 데이터를 다시 불러와 화면 갱신 및 알림
  useEffect(() => {
    if (naverBatchStatusUpdateDone) {
        window.alert(naverBatchStatusUpdateData?.msg || "네이버페이 배치 설정이 모든 브랜드에 일괄 적용되었습니다.");
        dispatch(loadSystemSettingAction());
        dispatch(updateSystemStatusResetAction());
    }
  }, [naverBatchStatusUpdateDone, naverBatchStatusUpdateData, dispatch]);

  useEffect(() => {
    if (pixelStatusUpdateDone) {
        window.alert(pixelStatusUpdateData?.msg || "픽셀 설정이 모든 브랜드에 일괄 적용되었습니다.");
        dispatch(loadSystemSettingAction());
        dispatch(updateSystemStatusResetAction());
    }
  }, [pixelStatusUpdateDone, pixelStatusUpdateData, dispatch]);

  useEffect(() => {
    return () => {
        dispatch(updateSystemStatusResetAction());
    };
  }, [dispatch]);

  useEffect(() => {
    if (systemSetting) {
        setGlobalConfig({
            naverBatchConfig: {
                status: !!systemSetting.naverBatchStatus,
                schedule: systemSetting.schedule || []
            },
            pixelConfig: {
                facebook: !!systemSetting.facebookPixelStatus,
                tiktok: !!systemSetting.tiktokPixelStatus
            }
        });
    }
  }, [systemSetting]);

  const [globalConfig, setGlobalConfig] = useState({
    naverBatchConfig: {
        status: true,
        schedule: []
    },
    pixelConfig: {
        facebook: false,
        tiktok: false
    }
  });

  const addSchedule = () => {
      setGlobalConfig(prev => ({
          ...prev,
          naverBatchConfig: {
              ...prev.naverBatchConfig,
              schedule: [
                  ...prev.naverBatchConfig.schedule,
                  { start: '', end: '', memo: '' }
              ]
          }
      }));
  };

  const removeSchedule = (index) => {
      setGlobalConfig(prev => {
          const newSchedule = [...prev.naverBatchConfig.schedule];
          newSchedule.splice(index, 1);
          return {
              ...prev,
              naverBatchConfig: {
                  ...prev.naverBatchConfig,
                  schedule: newSchedule
              }
          };
      });
  };

  const updateSchedule = (index, field, value) => {
      setGlobalConfig(prev => {
          const newSchedule = [...prev.naverBatchConfig.schedule];
          newSchedule[index] = {
              ...newSchedule[index],
              [field]: value
          };
          return {
              ...prev,
              naverBatchConfig: {
                  ...prev.naverBatchConfig,
                  schedule: newSchedule
              }
          };
      });
  };

  const saveGlobalConfig = useCallback(async (type) => {
    try {
        if (!window.confirm("모든 브랜드에 이 설정이 일괄 적용됩니다. 진행하시겠습니까?")) return;

        let data = { isGlobal: true };

        if (type === 'batch') {
            data = {
                ...data,
                status: globalConfig.naverBatchConfig.status,
                schedule: globalConfig.naverBatchConfig.schedule
            };
            dispatch(naverBatchStatusUpdateAction(data));
        } else {
            data = {
                ...data,
                ...globalConfig.pixelConfig
            };
            dispatch(pixelStatusUpdateAction(data));
        }

        // Saga에서 성공 처리가 완료되면 useEffect를 통해 알림이 표시됩니다.
    } catch (e) {
        window.alert("설정 적용 중 오류가 발생했습니다.");
        console.error(e);
    }
  }, [globalConfig, dispatch]);

  return loadMeLoading ? (
    null
  ) : (
    (loadMeDone || loadMeError) && (
      loadSystemSettingLoading && !systemSetting ? (
        <Loader />
      ) : (
        <div>
          <UIKit.Div classOptions={'uk-width-1-1'}>
            <Header title={'시스템 설정'} />
          </UIKit.Div>
            
            {/* Global Config Panels */}
            <div className="uk-grid-small" data-uk-grid>
                {/* Naver Batch Card */}
                <div className="uk-width-1-2@m">
                    <UIKit.Card cardOptions={'uk-card-default uk-card-style uk-margin-medium-top'}>
                        <div className="uk-flex uk-flex-between uk-flex-middle uk-margin-small-bottom">
                            <h5 className="uk-heading-bullet uk-margin-remove">네이버페이 배치 설정</h5>
                            <button 
                                className="uk-button uk-button-primary uk-button-small uk-border-rounded" 
                                style={{fontSize: '0.75rem', minHeight: '30px'}}
                                onClick={() => saveGlobalConfig('batch')}
                            >
                                배치 설정 일괄 적용
                            </button>
                        </div>
                        <div className="uk-background-muted uk-padding-small uk-border-rounded uk-margin-small-bottom">
                            <div className="uk-flex uk-flex-middle uk-flex-between uk-background-default uk-padding-small uk-border-rounded uk-box-shadow-small" style={{padding: '12px 15px'}}>
                                <span className="uk-text-small uk-text-bold">네이버페이 배치 활성화</span>
                                <Switch 
                                    checked={globalConfig.naverBatchConfig.status} 
                                    onChange={(e) => setGlobalConfig({...globalConfig, naverBatchConfig: {...globalConfig.naverBatchConfig, status: e.target.checked}})} 
                                />
                            </div>
                        </div>
                        
                        <div className="uk-margin-medium-top">
                            <div className="uk-flex uk-flex-between uk-flex-middle uk-margin-small-bottom">
                            <h5 className="uk-heading-bullet uk-margin-remove">네이버페이 배치 중단 스케줄</h5>
                                <button type="button" className="uk-button uk-button-default uk-button-small uk-border-rounded uk-box-shadow-hover-small" style={{fontSize: '0.75rem', padding: '0 10px', minHeight: '30px'}} onClick={addSchedule}>
                                    <i className="mdi mdi-plus uk-margin-small-right"></i>스케줄 추가
                                </button>
                            </div>
                            <div className="uk-background-muted uk-padding-small uk-border-rounded" style={{border: '1px dashed #ccc', minHeight: '80px'}}>
                                {globalConfig.naverBatchConfig.schedule.length === 0 ? (
                                    <div className="uk-flex uk-flex-center uk-flex-middle" style={{height: '60px'}}>
                                        <span className="uk-text-meta">등록된 스케줄이 없습니다.</span>
                                    </div>
                                ) : (
                                    <div className="uk-margin-remove-bottom">
                                        {globalConfig.naverBatchConfig.schedule.map((item, index) => {
                                        const [startDate = '', rawStartTime = '00:00:00'] = (item.start || '').split('T');
                                        const [endDate = '', rawEndTime = '00:00:00'] = (item.end || '').split('T');
    
                                        // 시:분만 추출하여 셀렉트박스와 매칭
                                        const startTime = rawStartTime.substring(0, 5);
                                        const endTime = rawEndTime.substring(0, 5);
    
                                        const handleDateTimeUpdate = (field, type, value) => {
                                            const currentVal = item[field] || 'T00:00:00';
                                            const parts = currentVal.split('T');
                                            let newDate = parts[0] || '';
                                            let newTime = (parts[1] || '00:00:00').substring(0, 5); // 기존 초 정보가 있으면 제거
    
                                            if (type === 'date') newDate = value;
                                            if (type === 'time') newTime = value;
    
                                            // 저장할 때는 반드시 :00 초를 붙여서 저장
                                            updateSchedule(index, field, `${newDate}T${newTime}:00`);
                                        };
    
                                        return (
                                            <div key={index} className={`uk-flex uk-flex-middle uk-background-default uk-padding-small uk-border-rounded uk-box-shadow-small ${index === globalConfig.naverBatchConfig.schedule.length - 1 ? '' : 'uk-margin-small-bottom'}`} style={{padding: '8px 12px'}}>
                                                <div className="uk-width-auto uk-margin-small-right">
                                                    <span className="uk-text-meta uk-text-bold" style={{color: '#1e87f0'}}>{index + 1}.</span>
                                                </div>
                                                
                                                {/* 시작 시간 */}
                                                <input 
                                                    className="uk-input uk-form-small"
                                                    style={{width: '125px'}}
                                                    type="date" 
                                                    value={startDate} 
                                                    onChange={(e) => handleDateTimeUpdate('start', 'date', e.target.value)}
                                                />
                                                <select 
                                                    className="uk-select uk-form-small uk-margin-small-left"
                                                    style={{width: '75px'}}
                                                    value={startTime}
                                                    onChange={(e) => handleDateTimeUpdate('start', 'time', e.target.value)}
                                                >
                                                    {TIME_OPTIONS.map(time => (
                                                        <option key={time} value={time}>{time}</option>
                                                    ))}
                                                </select>
    
                                                <span className="uk-margin-small-left uk-margin-small-right uk-text-muted">~</span>
    
                                                {/* 종료 시간 */}
                                                <input 
                                                    className="uk-input uk-form-small"
                                                    style={{width: '125px'}}
                                                    type="date" 
                                                    value={endDate} 
                                                    onChange={(e) => handleDateTimeUpdate('end', 'date', e.target.value)}
                                                />
                                                <select 
                                                    className="uk-select uk-form-small uk-margin-small-left"
                                                    style={{width: '75px'}}
                                                    value={endTime}
                                                    onChange={(e) => handleDateTimeUpdate('end', 'time', e.target.value)}
                                                >
                                                    {TIME_OPTIONS.map(time => (
                                                        <option key={time} value={time}>{time}</option>
                                                    ))}
                                                </select>
    
                                                {/* 메모 */}
                                                <div className="uk-flex-1 uk-margin-small-left uk-margin-small-right">
                                                    <input 
                                                        className="uk-input uk-form-small"
                                                        type="text"
                                                        placeholder="메모 입력"
                                                        value={item.memo || ''}
                                                        onChange={(e) => updateSchedule(index, 'memo', e.target.value)}
                                                    />
                                                </div>
    
                                                {/* 삭제 버튼 */}
                                                <div className="uk-width-auto">
                                                    <button 
                                                        type="button" 
                                                        className="uk-button uk-button-danger uk-button-small uk-border-rounded"
                                                        style={{padding: '0 10px', minHeight: '28px', lineHeight: '28px'}}
                                                        onClick={() => removeSchedule(index)}
                                                    >
                                                        삭제
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </UIKit.Card>
                </div>
    
                {/* Pixel Config Card */}
                <div className="uk-width-1-2@m">
                    <UIKit.Card cardOptions={'uk-card-default uk-card-style uk-margin-medium-top'}>
                        <div className="uk-flex uk-flex-between uk-flex-middle uk-margin-small-bottom">
                            <h5 className="uk-heading-bullet uk-margin-remove">픽셀 설정</h5>
                            <button 
                                className="uk-button uk-button-primary uk-button-small uk-border-rounded" 
                                style={{fontSize: '0.75rem', minHeight: '30px'}}
                                onClick={() => saveGlobalConfig('pixel')}
                            >
                                픽셀 설정 일괄 적용
                            </button>
                        </div>
                        <div className="uk-background-muted uk-padding-small uk-border-rounded" style={{minHeight: '120px'}}>
                            <div className="uk-flex uk-flex-middle uk-flex-between uk-background-default uk-padding-small uk-border-rounded uk-box-shadow-small uk-margin-small-bottom" style={{padding: '12px 15px'}}>
                                <span className="uk-text-small uk-text-bold">Facebook Pixel</span>
                                <Switch 
                                    checked={globalConfig.pixelConfig.facebook} 
                                    onChange={(e) => setGlobalConfig({...globalConfig, pixelConfig: {...globalConfig.pixelConfig, facebook: e.target.checked}})} 
                                />
                            </div>
                            <div className="uk-flex uk-flex-middle uk-flex-between uk-background-default uk-padding-small uk-border-rounded uk-box-shadow-small" style={{padding: '12px 15px'}}>
                                <span className="uk-text-small uk-text-bold">TikTok Pixel</span>
                                <Switch 
                                    checked={globalConfig.pixelConfig.tiktok} 
                                    onChange={(e) => setGlobalConfig({...globalConfig, pixelConfig: {...globalConfig.pixelConfig, tiktok: e.target.checked}})} 
                                />
                            </div>
                        </div>
                    </UIKit.Card>
                </div>
            </div>
        </div>
      )
    )
  );
};

const Switch = ({checked, onChange}) => (
  <label className="uk-switch" style={{cursor: 'pointer', display: 'inline-block', position: 'relative', width: '40px', height: '20px', marginBottom: 0, verticalAlign: 'middle'}}>
    <input 
      type="checkbox" 
      checked={checked} 
      onChange={onChange} 
      style={{opacity: 0, width: 0, height: 0}}
    />
    <span 
      style={{
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        backgroundColor: checked ? '#1e87f0' : '#ccc', 
        borderRadius: '34px', 
        transition: '.4s'
      }}
    ></span>
    <span 
      style={{
        position: 'absolute', 
        content: '""', 
        height: '14px', 
        width: '14px', 
        left: checked ? '22px' : '4px', 
        bottom: '3px', 
        backgroundColor: 'white', 
        borderRadius: '50%', 
        transition: '.4s'
      }}
    ></span>
  </label>
);

export default System;
